import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { usePreferenceStore } from '@/stores/usePreferenceStore';
import { SUPPORTED_LOCALES, type Locale } from '@/types/common';
import { firstMvp } from '@/lib/productScope';
import {
  loadChineseTranslations,
  loadChineseEntryTranslations,
  loadEnglishPageTranslations,
} from './translationResources';
import enCommon from './locales/en/common.json';
import enHome from './locales/en/home.json';
import enScreens from './locales/en/screens.json';

import enAuth from './locales/en/auth.json';
import enSystem from './locales/en/system.json';
import enSupport from './locales/en/support.json';

/** Entry, layout headers and native route recovery stay available without page resources. */
export const resources = {
  en: {
    common: enCommon,
    home: enHome,
    screens: enScreens,
    auth: enAuth,
    system: enSystem,
    support: enSupport,
  },
} as const;

/** 브라우저 값(zh, zh-Hans-CN, en-US …)을 지원 언어로 바꾼다. */
export function toSupportedLocale(value?: string | null): Locale | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase();
  if (normalized.startsWith('zh')) return 'zh-CN';
  if (normalized.startsWith('en')) return 'en';
  return undefined;
}

/**
 * 사용자 확정(2026-10-07): 저장한 언어 → 명시적 ?lang= → 영어.
 * 브라우저 언어로 첫 대문을 바꾸지 않으며 API와 화면이 같은 언어를 사용한다.
 */
export async function initI18n() {
  const preference = usePreferenceStore.getState();
  const locale =
    preference.locale ??
    toSupportedLocale(
      new URLSearchParams(window.location.search).get('lang'),
    ) ??
    'en';
  if (!preference.locale) preference.setLocale(locale);
  const selectedResources =
    locale === 'zh-CN'
      ? {
          ...resources,
          'zh-CN': await (firstMvp && window.location.pathname === '/'
            ? loadChineseEntryTranslations()
            : loadChineseTranslations()),
        }
      : resources;
  return i18n.use(initReactI18next).init({
    resources: selectedResources,
    lng: locale,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: Object.keys(resources.en),
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
}

/** Prepare a selected language completely before exposing its labels. */
export async function ensureLocaleTranslations(locale: string) {
  if (locale !== 'zh-CN') return;
  const namespaces = await loadChineseTranslations();
  for (const [namespace, values] of Object.entries(namespaces))
    i18n.addResourceBundle('zh-CN', namespace, values, true, true);
}

/** Home language controls need only public labels already available on that home. */
export async function ensureLocaleEntryTranslations(locale: string) {
  if (locale !== 'zh-CN') return;
  const namespaces = await loadChineseEntryTranslations();
  for (const [namespace, values] of Object.entries(namespaces))
    i18n.addResourceBundle('zh-CN', namespace, values, true, true);
}

/** Other pages share public English fallback labels; no preference or private data changes. */
export async function ensureEnglishPageTranslations() {
  const namespaces = await loadEnglishPageTranslations();
  for (const [namespace, values] of Object.entries(namespaces))
    i18n.addResourceBundle('en', namespace, values, true, true);
}

/** Prepare page fallback and the current language before any page content mounts. */
export async function ensurePageTranslations(locale: string) {
  await Promise.all([
    ensureEnglishPageTranslations(),
    ensureLocaleTranslations(locale),
  ]);
}

export default i18n;
